/**
 * Billing > Usage (Phase 21 / Task 21.7c): every billable customer's meters for a month,
 * rolled up in the billing time zone. GET /v1/platform/billing/usage?month=YYYY-MM.
 *
 * Every meter is shown, priced or not (D3). A meter with no data reads "no count taken", one with
 * no source "not collected", each with hover help (OPEN-197), and a partly captured one says how
 * many days it has: never a zero that was not counted. "Take today's snapshot" (POST /usage/snapshot) is for the dev walk;
 * the nightly sweeper takes it otherwise, and a repeat on the same day does nothing.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { fieldStyle, subText } from '../../ui/bits';
import { useBillingToday, useSnapshotNow, useUsage, type UsageMeter } from './billing-hooks';
import { billingErrorText } from './billing-format';
import { meterCell, meterHint, previousMonth } from './invoice-format';
import { METER_LABELS } from './rate-card-draft';

/** A meter's cell; one with no number, or a partial count, carries why as hover help. */
function MeterCell({ m }: { m: UsageMeter }): React.JSX.Element {
  const hint = meterHint(m);
  if (hint === null) return <>{meterCell(m)}</>;
  return (
    <span
      title={hint}
      style={{
        ...(m.value === null ? subText : {}),
        textDecoration: 'underline dotted',
        cursor: 'help',
      }}
    >
      {meterCell(m)}
    </span>
  );
}

function UsageTable({ month }: { month: string }): React.JSX.Element {
  const { data, isLoading, isError } = useUsage(month);
  if (isLoading) return <p style={subText}>Loading…</p>;
  if (isError || data === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)' }}>
        Could not load the usage for {month}.
      </p>
    );
  }
  const meters = [...new Set(data.customers.flatMap((c) => c.meters.map((m) => m.meter)))];
  return (
    <div style={{ overflowX: 'auto' }}>
      <p style={subText}>
        {data.periodStart} to {data.periodEnd} ({data.timezone}), {data.daysInPeriod} days.
      </p>
      <table style={{ borderCollapse: 'collapse', fontSize: '13px', minWidth: '100%' }}>
        <thead>
          <tr style={{ textAlign: 'left', ...subText }}>
            <th style={{ padding: '6px 8px' }}>Customer</th>
            {meters.map((m) => (
              <th key={m} style={{ padding: '6px 8px' }}>
                {METER_LABELS[m] ?? m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.customers.map((c) => (
            <tr
              key={c.tenantId}
              data-testid={`usage-${c.tenantId}`}
              style={{ borderTop: '1px solid var(--color-border-primary)' }}
            >
              <td style={{ padding: '6px 8px' }}>{c.tenantName}</td>
              {meters.map((name) => {
                const m = c.meters.find((x) => x.meter === name);
                return (
                  <td key={name} style={{ padding: '6px 8px' }}>
                    {m === undefined ? '—' : <MeterCell m={m} />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageForMonth({ today }: { today: string }): React.JSX.Element {
  const [month, setMonth] = React.useState(previousMonth(today));
  const snapshot = useSnapshotNow({
    onSuccess: () => toast.success("Today's usage snapshot is taken."),
    onError: (err) => toast.error(billingErrorText(err, 'Could not take the snapshot.')),
  });
  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
        <label style={{ ...subText, display: 'flex', gap: '6px', alignItems: 'center' }}>
          Month
          <input
            type="month"
            aria-label="Usage month"
            value={month}
            onChange={(e): void => setMonth(e.target.value)}
            style={{ ...fieldStyle, width: 'auto' }}
          />
        </label>
        <Button type="button" variant="outline" onClick={(): void => snapshot.mutate({})}>
          Take today&apos;s snapshot
        </Button>
      </div>
      {month !== '' && <UsageTable month={month} />}
    </div>
  );
}

export function UsagePage(): React.JSX.Element {
  const today = useBillingToday();
  if (today === null) return <p style={subText}>Loading…</p>;
  return <UsageForMonth today={today} />;
}
